import { useState } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';

const navItems = [
  { path: '/student/dashboard', label: 'Dashboard', icon: 'fa-th-large' },
  { path: '/student/profile', label: 'My Profile', icon: 'fa-user' },
  { path: '/student/results', label: 'My Results', icon: 'fa-award' },
  { path: '/student/payments', label: 'Payments', icon: 'fa-credit-card' },
  { path: '/student/subjects', label: 'Subjects', icon: 'fa-book' },
  { path: '/student/notifications', label: 'Notifications', icon: 'fa-bell' },
];

const StudentLayout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { logout, user } = useAuth();

  const student = user?.user || {};
  const unreadCount = 0;

  const handleLogout = () => {
    logout?.();
    navigate('/student/login');
  };

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed lg:static inset-y-0 left-0 z-50 w-72 bg-white border-r border-slate-200 transform transition-transform duration-300 ease-in-out ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        } flex flex-col`}
      >
        {/* Logo */}
        <div className="p-6 border-b border-slate-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-emerald-600 to-emerald-800 rounded-xl flex items-center justify-center text-white shadow-lg">
              <i className="fas fa-graduation-cap text-lg" />
            </div>
            <div>
              <h1 className="font-bold text-slate-800 text-sm leading-tight">Islamic School</h1>
              <p className="text-xs text-slate-400">Student Portal</p>
            </div>
          </div>
        </div>

        {/* Student Mini Profile */}
        <div className="p-4 mx-4 mt-4 bg-gradient-to-br from-emerald-50 to-teal-50 rounded-2xl border border-emerald-100">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-emerald-200 rounded-full flex items-center justify-center text-emerald-700 font-bold text-lg">
              {(student.full_name || 'S').charAt(0)}
            </div>
            <div className="overflow-hidden">
              <p className="font-semibold text-slate-800 text-sm truncate">{student.full_name || 'Student'}</p>
              <p className="text-xs text-slate-500 font-mono">{student.admission_number || ''}</p>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          {navItems.map((item) => {
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setSidebarOpen(false)}
                className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all ${
                  isActive
                    ? 'bg-emerald-600 text-white shadow-md shadow-emerald-200'
                    : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                }`}
              >
                <i className={`fas ${item.icon} w-5 text-center ${isActive ? 'text-white' : 'text-slate-400'}`} />
                {item.label}
                {item.label === 'Notifications' && unreadCount > 0 && (
                  <span className={`ml-auto text-xs font-bold px-2 py-0.5 rounded-full ${isActive ? 'bg-white text-emerald-600' : 'bg-red-500 text-white'}`}>
                    {unreadCount}
                  </span>
                )}
              </Link>
            );
          })}
        </nav>

        {/* Logout */}
        <div className="p-4 border-t border-slate-100">
          <button
            onClick={handleLogout}
            className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-red-600 hover:bg-red-50 transition-all w-full"
          >
            <i className="fas fa-sign-out-alt w-5 text-center" />
            Logout
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top Navbar */}
        <header className="bg-white border-b border-slate-200 px-4 lg:px-8 py-4 sticky top-0 z-30">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => setSidebarOpen(true)}
                className="lg:hidden p-2 rounded-lg hover:bg-slate-100 text-slate-600"
              >
                <i className="fas fa-bars text-lg" />
              </button>
              <div>
                <h2 className="text-lg font-bold text-slate-800">
                  {navItems.find((n) => n.path === location.pathname)?.label || 'Dashboard'}
                </h2>
                <p className="text-xs text-slate-500 hidden sm:block">
                  {new Date().toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              {/* Notification Bell */}
              <Link
                to="/student/notifications"
                className="relative p-2.5 rounded-xl hover:bg-slate-100 text-slate-600 transition-colors"
              >
                <i className="fas fa-bell text-lg" />
                {unreadCount > 0 && (
                  <span className="absolute top-1 right-1 w-5 h-5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                    {unreadCount}
                  </span>
                )}
              </Link>

              {/* Profile */}
              <Link
                to="/student/profile"
                className="flex items-center gap-3 pl-3 pr-4 py-2 rounded-xl hover:bg-slate-100 transition-colors"
              >
                <div className="w-9 h-9 bg-gradient-to-br from-emerald-500 to-emerald-700 rounded-full flex items-center justify-center text-white font-bold text-sm">
                  {(student.full_name || 'S').charAt(0)}
                </div>
                <div className="hidden sm:block text-left">
                  <p className="text-sm font-semibold text-slate-800 leading-tight">{student.full_name || 'Student'}</p>
                  <p className="text-xs text-slate-500">{student.class || ''}</p>
                </div>
              </Link>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 p-4 lg:p-8 overflow-y-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default StudentLayout;
